import React from "react";
import { motion } from "framer-motion";
import { EASE } from "@/components/primitives";
import { cn } from "@/lib/utils";

const Node = ({ index, active }) => (
  <span className="relative flex h-14 w-14 items-center justify-center">
    <span className="absolute inset-0 rounded-full bg-axor-cyan/15 blur-md" />
    <svg viewBox="0 0 56 56" className="relative h-full w-full">
      <polygon
        points="28,6 50,46 6,46"
        fill="none"
        stroke="#16B8D4"
        strokeWidth="1.5"
        opacity="0.5"
      />
      <polygon points="28,16 40,40 16,40" fill="#16B8D4" opacity="0.9" />
    </svg>
    <span className="absolute text-xs font-bold text-axor-hero">{index}</span>
  </span>
);

/**
 * steps: [{ number, title, description }]
 * Horizontal on desktop, vertical on mobile.
 */
export const Timeline = ({ steps }) => (
  <div data-testid="career-timeline">
    {/* Desktop */}
    <div className="relative hidden lg:block">
      <div className="absolute left-0 right-0 top-7 h-px bg-gradient-to-r from-transparent via-axor-cyan/40 to-transparent" />
      <div className="grid grid-cols-5 gap-6">
        {steps.map((s, i) => (
          <motion.div
            key={s.number}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: EASE, delay: i * 0.12 }}
            className="flex flex-col items-start"
          >
            <Node index={s.number} />
            <h3 className="mt-6 text-lg font-semibold tracking-tight text-axor-white">
              {s.title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-axor-text">
              {s.description}
            </p>
          </motion.div>
        ))}
      </div>
    </div>

    {/* Mobile / tablet vertical */}
    <div className="relative lg:hidden">
      <div className="absolute bottom-6 left-7 top-6 w-px bg-gradient-to-b from-axor-cyan/40 via-axor-cyan/20 to-transparent" />
      <div className="space-y-8">
        {steps.map((s, i) => (
          <motion.div
            key={s.number}
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, ease: EASE, delay: i * 0.08 }}
            className="flex gap-5"
          >
            <Node index={s.number} />
            <div className="pt-1.5">
              <h3 className="text-lg font-semibold tracking-tight text-axor-white">
                {s.title}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-axor-text">
                {s.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </div>
);

export const careerSteps = [
  { number: "01", title: "Discover", description: "Understand your goals." },
  { number: "02", title: "Prepare", description: "Strengthen your professional profile." },
  { number: "03", title: "Connect", description: "Discover relevant opportunities." },
  { number: "04", title: "Interview", description: "Prepare with confidence." },
  { number: "05", title: "Advance", description: "Move toward your next opportunity." },
];
